import { Facebook, Youtube, Instagram, Linkedin } from "lucide-react";
import { getUtmContent } from "@/lib/utm";
import { getRef } from "@/lib/ref";

export const SITE = "https://hearthvault.family";
// Where this social front door lives once deployed (a subdomain of the product).
export const FOLLOW_SITE = "https://follow.hearthvault.family";

// Swap these placeholder profile URLs for the real ones when the pages exist.
export const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/",
  youtube: "https://www.youtube.com/",
  instagram: "https://www.instagram.com/",
  linkedin: "https://www.linkedin.com/",
};

// Carries utm_content (which post / video sent them) and any partner ref code through to hearthvault.family.
export const signupUrl = (source, content = getUtmContent()) => {
  const ref = getRef();
  const medium = ref ? "partner" : "social";
  return `${SITE}/welcome?utm_source=${source}&utm_medium=${medium}&utm_campaign=hearthside${content ? `&utm_content=${encodeURIComponent(content)}` : ""}${ref ? `&ref=${ref}` : ""}`;
};

// Swap youtubeId for the real 60-second tour video ID (the part after `v=` in the YouTube URL).
export const TOUR_VIDEO = {
  youtubeId: "REPLACE_WITH_YOUTUBE_VIDEO_ID",
  title: "The 60-second tour",
  caption:
    "Drop in a receipt, a policy and a will. Watch them come back named, dated and filed, then see the reminder that fires before anything lapses. One minute, start to finish.",
  duration: "1:00",
  poster: "/img/youtube.jpg",
  posterAlt: "Hands lifting an old letter out of a red keepsake tin",
  // Chapter start times in seconds. Edit to match the real cut.
  chapters: [
    { label: "Drop it in", at: 0, d: "A receipt, a policy and a will go into the vault as they are." },
    { label: "Filed and named", at: 20, d: "They come back named, dated and in the right folder, for you to approve." },
    { label: "Reminded", at: 45, d: "The reminder fires before the policy lapses, not after." },
  ],
};

export const NETWORKS = [
  {
    key: "facebook",
    name: "Facebook",
    Icon: Facebook,
    accent: "#2563EB",
    image: "/img/facebook.jpg",
    imageAlt: "Grandmothers and granddaughters sitting together on a sofa",
    audience: "Families & caregivers",
    teaser: "Checklists and conversations for the people who hold the family folder.",
    eyebrow: "Welcome, from Facebook",
    headline: ["The family folder,", "finally in one", "warm place."],
    intro:
      "You found us in your feed. Here is the rest of the story: HearthVault names and files every paper your family will need, remembers the renewals, and turns it all into a plan your partner or executor can follow on the worst day.",
    posts: [
      { t: "Kitchen-table checklists", d: "What to gather before the conversation with Mom and Dad, one printable at a time." },
      { t: "Caregiver questions, answered", d: "Real questions from adult children of aging parents, and how the vault handles them." },
      { t: "Quiet product news", d: "New features, explained in plain words, never pushy." },
    ],
    title: "HearthVault on Facebook — checklists for the family folder",
    description:
      "Arrived from Facebook? Meet HearthVault, the family document safe that files wills, policies and receipts, reminds you before dates lapse, and hands the right bundle to the right person.",
    video: TOUR_VIDEO,
  },
  {
    key: "youtube",
    name: "YouTube",
    Icon: Youtube,
    accent: "#DC2626",
    image: "/img/youtube.jpg",
    imageAlt: "Hands lifting an old letter out of a red keepsake tin",
    audience: "Watch it work",
    teaser: "Walkthroughs of the vault: drop it in, filed and named, reminded in time.",
    eyebrow: "Welcome, from YouTube",
    headline: ["Shoebox to sorted,", "in about", "one minute."],
    intro:
      "Drop in a receipt, a policy and a will. Watch them come back named, dated and filed, then see the reminder that fires before the policy lapses. That's the whole idea, and our channel shows every step of it.",
    posts: [
      { t: "The 60-second tour", d: "A walk through the vault, start to finish." },
      { t: "How-to walkthroughs", d: "Setting up family access, executor profiles and emergency access." },
      { t: "Estate basics, explained", d: "What each estate document does and where the original should live." },
    ],
    title: "HearthVault on YouTube — watch the family vault at work",
    description:
      "Arrived from YouTube? See how HearthVault turns receipts, policies and wills into a filed, dated family vault, then create yours free in about two minutes.",
    video: TOUR_VIDEO,
  },
  {
    key: "instagram",
    name: "Instagram",
    Icon: Instagram,
    accent: "#C026D3",
    image: "/img/instagram.jpg",
    imageAlt: "A vintage tin of envelopes and postcards resting on a sofa",
    audience: "Small, calm habits",
    teaser: "Bite-sized life-admin rituals, one carousel at a time.",
    eyebrow: "Welcome, from Instagram",
    headline: ["Life admin,", "kept small", "and kept calm."],
    intro:
      "Every family has a drawer, a shoebox or a \"somewhere on Dad's laptop.\" On Instagram we share small rituals for emptying it. In the app, HearthVault does the filing for you, and you approve every filing.",
    posts: [
      { t: "Five-minute rituals", d: "One paper, one habit, every week." },
      { t: "Before & after", d: "Messy drawers turned into filed, dated vaults." },
      { t: "Reminders that matter", d: "Gentle nudges on renewals, tax season and the papers people forget." },
    ],
    title: "HearthVault on Instagram — small, calm life-admin rituals",
    description:
      "Arrived from Instagram? HearthVault is the family document safe that files your important papers with AI, reminds you before dates lapse and keeps everything ready.",
  },
  {
    key: "linkedin",
    name: "LinkedIn",
    Icon: Linkedin,
    accent: "#0A66C2",
    image: "/img/linkedin.jpg",
    imageAlt: "Two people reviewing and signing documents at a desk",
    audience: "Advisors & attorneys",
    teaser: "For the professionals who build the plan and want it to stay current.",
    eyebrow: "Welcome, from LinkedIn",
    headline: ["Keep the plan", "you built", "current."],
    intro:
      "Hand clients a vault that keeps the plan you built up to date, with a portal that shows you what's missing. Executors get the will, the accounts and the plan in one read-only profile, before the day they need them.",
    posts: [
      { t: "Notes for advisors", d: "How families keep their estate documents signed, current and findable." },
      { t: "Executor readiness", d: "What a clean handover looks like, and the steps that get skipped." },
      { t: "Company updates", d: "Security posture, product milestones and the team behind HearthVault." },
    ],
    title: "HearthVault on LinkedIn — for advisors, attorneys and executors",
    description:
      "Arrived from LinkedIn? HearthVault gives clients a family vault that keeps estate plans current, with read-only executor profiles and a portal that shows what's missing.",
    secondary: { label: "HearthVault for advisors", href: `${SITE}/for-advisors?utm_source=linkedin&utm_medium=social&utm_campaign=hearthside` },
  },
];

export const getNetwork = (key) => NETWORKS.find((n) => n.key === key);
